import { Monitor, X } from 'lucide-react';

import { useWorkshopControl } from '../../hooks/useWorkshopControl';
import { closeWorkshopScreen, toggleWorkshopScreen, useWorkshopScreenOpen } from '../../lib/workshop/screenWindow';
import { WorkshopBrowser } from '../mission/MissionWorkshopControl';

/**
 * The workshop's screen one click from Otto (the tools' header): the button of the one window
 * (`WorkshopScreenHost`). The Bureau's sidebar and the small Otto's « Voir son écran » drive the
 * same window (`lib/workshop/screenWindow`).
 */
export function WorkshopScreenDock() {
  const open = useWorkshopScreenOpen();
  return (
    <button
      type="button"
      onClick={toggleWorkshopScreen}
      aria-expanded={open}
      aria-label="Écran de l'atelier"
      title="Écran de l'atelier (navigateur de la machine)"
      className={`flex items-center p-2 rounded-lg transition-colors ${
        open ? 'text-[#0066FF] bg-blue-50' : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'
      }`}
    >
      <Monitor className="h-4 w-4" />
    </button>
  );
}

/**
 * The workshop's screen: a floating window, not a modal — the page stays usable underneath. Mounted
 * once by AdminLayout, so the window and its live session stay open across every page until closed.
 * It connects on opening: to act if the database allows it, else to watch.
 */
export function WorkshopScreenHost() {
  const open = useWorkshopScreenOpen();
  return open ? <WorkshopScreenWindow onClose={closeWorkshopScreen} /> : null;
}

function WorkshopScreenWindow({ onClose }: { onClose: () => void }) {
  const control = useWorkshopControl(null);
  return (
    <section
      role="dialog"
      aria-label="Écran de l'atelier"
      className="fixed right-2 top-16 z-50 max-h-[calc(100vh-5rem)] w-[min(960px,calc(100vw-1rem))] overflow-auto rounded-[20px] bg-desk-card p-3 text-desk-ink shadow-pop"
    >
      <header className="mb-2 flex items-center gap-2">
        <Monitor size={16} className="text-desk-soft" aria-hidden="true" />
        <h2 className="text-sm font-medium">Écran de l'atelier</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer l'écran de l'atelier"
          className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg text-desk-soft hover:bg-desk-hover hover:text-desk-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>
      {control.read ? (
        <WorkshopBrowser threadId={null} control={control} autoOpen />
      ) : (
        <p className="text-xs text-desk-muted">{control.error ?? 'Lecture de la machine…'}</p>
      )}
    </section>
  );
}
